/**
 * shared/date-prefs.ts — PRD §6.4 preferences & consent (card sXseXczawWR1).
 *
 * Two members, two preference rows, one question the agent is allowed to
 * answer with: what did BOTH of them say? `intersectDatePrefs` is that
 * answer — a preference that only one partner saved drives nothing, which
 * is why the suggest path takes an intersection and never a single row.
 *
 * Location consent is the privacy edge: 'precise' is the only level that
 * stores coordinates, 'city' stores a label, 'off' stores neither, and the
 * partner's view of any of it is stripped to the consent level alone. The
 * derived mode banner (Together / Long-distance / Unspecified) is computed
 * here so the worker and the UI cannot disagree about it.
 */
import {
  DATE_CONSTRAINTS,
  DATE_COST_BANDS,
  DATE_ENERGY,
  DATE_FACTOR_ATTITUDES,
  DATE_LOCATION_CONSENTS,
  DATE_TIMINGS,
  type DateConstraint,
  type DateCostBand,
  type DateDerivedMode,
  type DateEnergy,
  type DateFactorAttitude,
  type DateLocationConsent,
  type DateMemberPrefs,
  type DatePrefsIntersection,
  type DateTiming,
} from './types.ts';

/** Card GMat5z0Zxutf: every factor sits at Neutral until the couple says
 *  otherwise — a fresh member prefers nothing and rules nothing out. */
export const DEFAULT_DATE_FACTOR_ATTITUDES: Record<DateConstraint, DateFactorAttitude> =
  Object.fromEntries(DATE_CONSTRAINTS.map((c) => [c, 'neutral'])) as Record<DateConstraint, DateFactorAttitude>;

/** What a member sees until they save: enough to render the panel, and
 *  deliberately unopinionated — no location, no assumptions. */
export const DEFAULT_DATE_PREFS: Omit<DateMemberPrefs, 'memberId' | 'updatedAt'> = {
  cadence: 2,
  suggestByDay: 5,
  timing: ['weekday_eve', 'weekend_day', 'weekend_eve'],
  modeRatio: 50,
  budget: '$$',
  energy: 'med',
  constraints: [],
  factorAttitudes: DEFAULT_DATE_FACTOR_ATTITUDES,
  locationConsent: 'off',
  locationLabel: null,
  lat: null,
  lng: null,
};

const CADENCE_MIN = 1;
const CADENCE_MAX = 4;
const DAY_MIN = 0;
const DAY_MAX = 6;
const RATIO_MIN = 0;
const RATIO_MAX = 100;

/** Budget bands are an ordered ceiling: 'free' ≤ '$' ≤ '$$' ≤ '$$$'. */
export function budgetRank(band: DateCostBand): number {
  const i = DATE_COST_BANDS.indexOf(band);
  return i < 0 ? 2 : i;
}

export function budgetCeiling(a: DateCostBand, b: DateCostBand): DateCostBand {
  return budgetRank(a) <= budgetRank(b) ? a : b;
}

const num = (v: unknown, lo: number, hi: number, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, Math.round(n)));
};

const enumOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;

const listOf = <T extends string>(v: unknown, allowed: readonly T[]): T[] | null => {
  if (!Array.isArray(v)) return null;
  const out: T[] = [];
  for (const item of v) {
    if (typeof item !== 'string') return null;
    if (!(allowed as readonly string[]).includes(item)) return null;
    if (!out.includes(item as T)) out.push(item as T);
  }
  return out;
};

/**
 * Merge a PUT body onto the member's current row. Unknown keys are ignored,
 * out-of-range numbers clamp to the range the panel offers, and a location
 * consent other than 'precise' is the one input that DELETES stored
 * coordinates — turning location off must leave nothing behind to serve.
 */
export function applyDatePrefsPatch(
  current: DateMemberPrefs,
  patch: Record<string, unknown>,
): DateMemberPrefs {
  const consent = 'locationConsent' in patch
    ? enumOf(patch.locationConsent, DATE_LOCATION_CONSENTS, current.locationConsent)
    : current.locationConsent;

  const timing = 'timing' in patch
    ? (listOf(patch.timing, DATE_TIMINGS) ?? current.timing)
    : current.timing;

  // Card GMat5z0Zxutf: the panel now speaks in per-factor tri-state
  // attitudes (Desirable / Neutral / Undesirable) instead of a rule-out
  // list. `constraints` is DERIVED from the Undesirable set so the
  // intersection and the scorer keep reading the exact list they always
  // did — the two can never disagree. If a caller still PUTs `constraints`
  // directly (the probe does), map it into attitudes so the store stays the
  // single source of truth.
  let constraints = current.constraints;
  let factorAttitudes = { ...current.factorAttitudes };
  if ('factorAttitudes' in patch && patch.factorAttitudes && typeof patch.factorAttitudes === 'object') {
    const incoming = patch.factorAttitudes as Record<string, unknown>;
    for (const c of DATE_CONSTRAINTS) {
      if (c in incoming) {
        const att = DATE_FACTOR_ATTITUDES.includes(incoming[c] as DateFactorAttitude)
          ? (incoming[c] as DateFactorAttitude)
          : 'neutral';
        factorAttitudes[c] = att;
      }
    }
    constraints = DATE_CONSTRAINTS.filter((c) => factorAttitudes[c] === 'undesirable');
  } else if ('constraints' in patch) {
    const list = listOf(patch.constraints, DATE_CONSTRAINTS);
    if (list) {
      constraints = list;
      for (const c of DATE_CONSTRAINTS) {
        factorAttitudes[c] = list.includes(c) ? 'undesirable' : 'neutral';
      }
    }
  }
  const attitudes = factorAttitudes;

  // City is a label the member types; precise is a fix from the browser.
  // Off (and, defensively, anything that is not precise or city) keeps no geo.
  let locationLabel = current.locationLabel;
  if (consent !== 'city') locationLabel = null;
  else if ('locationLabel' in patch) locationLabel = typeof patch.locationLabel === 'string' && patch.locationLabel.trim()
    ? patch.locationLabel.trim().slice(0, 60)
    : null;

  let lat = consent === 'precise' ? current.lat : null;
  let lng = consent === 'precise' ? current.lng : null;
  if (consent === 'precise' && 'lat' in patch && 'lng' in patch) {
    const plat = Number(patch.lat);
    const plng = Number(patch.lng);
    const valid = Number.isFinite(plat) && Number.isFinite(plng)
      && Math.abs(plat) <= 90 && Math.abs(plng) <= 180;
    lat = valid ? plat : null;
    lng = valid ? plng : null;
  }
  if (consent !== 'precise') {
    lat = null;
    lng = null;
  }

  return {
    ...current,
    cadence: 'cadence' in patch ? num(patch.cadence, CADENCE_MIN, CADENCE_MAX, current.cadence) : current.cadence,
    suggestByDay: 'suggestByDay' in patch ? num(patch.suggestByDay, DAY_MIN, DAY_MAX, current.suggestByDay) : current.suggestByDay,
    timing,
    modeRatio: 'modeRatio' in patch ? num(patch.modeRatio, RATIO_MIN, RATIO_MAX, current.modeRatio) : current.modeRatio,
    budget: 'budget' in patch ? enumOf(patch.budget, DATE_COST_BANDS, current.budget) : current.budget,
    energy: 'energy' in patch ? enumOf(patch.energy, DATE_ENERGY, current.energy) : current.energy,
    constraints,
    factorAttitudes: attitudes,
    locationConsent: consent,
    locationLabel,
    lat,
    lng,
    updatedAt: Date.now(),
  };
}

/**
 * prefs(A) ∩ prefs(B) — the ONLY input the suggest path is allowed to read.
 *
 * - Sets (timing) intersect: only slots both members cleared.
 * - Ceilings (budget) take the cheaper band; cadence takes the lower number —
 *   the partner who wants less never gets outvoted by the agent.
 * - Discrete agreements (suggest-by day, energy, mode ratio) survive only
 *   when both members said the same thing; otherwise there is no overlap.
 * - Constraints compound: one partner's restriction applies to both.
 *
 * Called only when BOTH members have a saved row — one row alone returns
 * nothing, so a lone preference can never steer a suggestion.
 */
export function intersectDatePrefs(a: DateMemberPrefs, b: DateMemberPrefs): DatePrefsIntersection {
  return {
    cadence: Math.min(a.cadence, b.cadence),
    suggestByDay: a.suggestByDay === b.suggestByDay ? a.suggestByDay : null,
    timing: DATE_TIMINGS.filter((t) => a.timing.includes(t) && b.timing.includes(t)),
    modeRatio: a.modeRatio === b.modeRatio ? a.modeRatio : null,
    budget: budgetCeiling(a.budget, b.budget),
    energy: a.energy === b.energy ? a.energy : null,
    constraints: [...new Set([...a.constraints, ...b.constraints])].sort() as DateConstraint[],
    // Both must have leaned, so a Desirable never becomes a rule by a single
    // member's hand — it only narrows what the concierge goes looking for
    // (see buildPlaceQuery's category ladder and the intersection contract).
    desirables: DATE_CONSTRAINTS.filter(
      (c) => a.factorAttitudes?.[c] === 'desirable' && b.factorAttitudes?.[c] === 'desirable',
    ),
  };
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

/** PRD §6.4: the banner both partners see, with the reason spelled out.
 *  A guess is never shown as a fact — incomplete or withheld location is
 *  'unspecified' and the reason says exactly what is missing. */
export function deriveDateMode(
  a: DateMemberPrefs | null,
  b: DateMemberPrefs | null,
): { mode: DateDerivedMode; reason: string } {
  if (!a || !b) return { mode: 'unspecified', reason: 'One of you has not set a location yet.' };
  const off = [a, b].find((p) => p.locationConsent === 'off');
  if (off) return { mode: 'unspecified', reason: 'Location is turned off for one of you, so there is nothing to compare.' };

  const bothCoords = a.lat != null && a.lng != null && b.lat != null && b.lng != null;
  if (bothCoords) {
    const km = haversineKm(a.lat as number, a.lng as number, b.lat as number, b.lng as number);
    return km > 100
      ? { mode: 'long_distance', reason: `You are about ${km} km apart.` }
      : { mode: 'together', reason: `You are about ${km} km apart.` };
  }

  const aCity = a.locationConsent === 'city' ? a.locationLabel : null;
  const bCity = b.locationConsent === 'city' ? b.locationLabel : null;
  if (aCity && bCity) {
    return aCity.toLowerCase() === bCity.toLowerCase()
      ? { mode: 'together', reason: `You are both in ${aCity}.` }
      : { mode: 'long_distance', reason: `You are in different cities (${aCity} and ${bCity}).` };
  }

  return {
    mode: 'unspecified',
    reason: 'We need the same kind of location from both of you (city or precise) to tell.',
  };
}

/** True when a band survives the intersection's budget ceiling. */
export function withinBudgetCeiling(band: DateCostBand, ceiling: DateCostBand): boolean {
  return budgetRank(band) <= budgetRank(ceiling);
}
